import {
  ChevronDown,
  ChevronRight,
  CircleCheck,
  Code2,
  Eye,
  FileText,
  Globe,
  HelpCircle,
  Image as ImageIcon,
  List,
  ListChecks,
  Loader2,
  Pencil,
  Plus,
  Search,
  Sparkles,
  Terminal,
  Trash2,
  Upload,
} from "lucide-react";
import { useState, type ComponentType } from "react";

import { cn } from "@bigpp/ui/lib/utils";

import type { ToolCallCard } from "../agent/useAgentRun.ts";
import { labelFor, ToolCardDetails } from "./ToolCard.tsx";

type IconType = ComponentType<{ className?: string }>;

const TOOL_ICON: Record<ToolCallCard["toolName"], IconType> = {
  execute_office_js_code: Code2,
  upload_current_deck_to_sandbox: Upload,
  insert_deck_slides: FileText,
  peek_slide: ImageIcon,
  use_skill: Sparkles,
  ask_user: HelpCircle,
  create_skill: Plus,
  edit_skill: Pencil,
  delete_skill: Trash2,
  list_skills: List,
  insert_attachment_image: ImageIcon,
  insert_sandbox_image: ImageIcon,
  insert_sandbox_deck_slides: FileText,
  code_interpreter: Terminal,
  file_search: Search,
  web_search: Globe,
  create_plan: ListChecks,
  jsx_preview: Eye,
};

// A single compact row inside the Task panel. The full ToolCard details
// live behind a per-row expand — keeps the firehose of execute_office_js_code calls
// from dominating the chat, but anything is one click away.
export function TaskRow({
  tc,
  shimmer,
}: {
  tc: ToolCallCard;
  shimmer: boolean;
}) {
  const [open, setOpen] = useState(false);
  const Icon = TOOL_ICON[tc.toolName];
  // Even when a tool call errors, we surface it as a completed step: the agent
  // usually recovers on its own and a red row reads as "the whole thing broke"
  // to the user. Full error text is still available via the expand.
  const StatusIcon = tc.status === "running" ? Loader2 : CircleCheck;
  const statusClass =
    tc.status === "running"
      ? "text-[var(--color-muted-foreground)] animate-spin"
      : "text-[var(--color-success)]";

  return (
    <li className="border-b border-[var(--color-border)] last:border-b-0">
      <button
        type="button"
        onClick={() => {
          setOpen((o) => !o);
        }}
        className="flex w-full items-center gap-2 px-2.5 py-1 text-[11px] hover:bg-[var(--color-secondary)] transition-colors"
      >
        <StatusIcon className={cn("size-3 shrink-0", statusClass)} />
        <Icon className="size-3 shrink-0 text-[var(--color-muted-foreground)]" />
        <span
          className={cn(
            "truncate min-w-0 text-left",
            tc.status === "running" && shimmer && "shimmer-text",
          )}
        >
          {labelFor(tc)}
        </span>
        <span className="ml-auto shrink-0">
          {open ? (
            <ChevronDown className="size-3 text-[var(--color-muted-foreground)]" />
          ) : (
            <ChevronRight className="size-3 text-[var(--color-muted-foreground)]" />
          )}
        </span>
      </button>
      {open && (
        <div className="px-2.5 pb-2 pt-1 bg-[var(--color-secondary)]/40">
          <ToolCardDetails tc={tc} />
        </div>
      )}
    </li>
  );
}
